import { supabase } from './supabase';

export type EmailEvent =
  | 'sales_report'
  | 'stock_balances_report'
  | 'accommodation_booking'
  | 'accommodation_report'
  | 'credit_settlement_email'
  | 'billing_notifications';

export interface EmailConfiguration {
  id: string;
  outlet_id: string;
  first_name: string;
  last_name: string;
  email: string;
  events: EmailEvent[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateEmailConfigRequest {
  outlet_id: string;
  first_name: string;
  last_name: string;
  email: string;
  events: EmailEvent[];
}

export interface UpdateEmailConfigRequest {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  events?: EmailEvent[];
  is_active?: boolean;
}

export async function fetchEmailConfigurations(
  outletId: string,
): Promise<EmailConfiguration[]> {
  const { data, error } = await supabase
    .from('outlet_email_configurations')
    .select('*')
    .eq('outlet_id', outletId)
    .order('created_at', { ascending: true });

  if (error) throw new Error(error.message);
  return (data ?? []) as EmailConfiguration[];
}

export async function createEmailConfiguration(
  request: CreateEmailConfigRequest,
): Promise<EmailConfiguration> {
  const { data, error } = await supabase
    .from('outlet_email_configurations')
    .insert({
      outlet_id: request.outlet_id,
      first_name: request.first_name,
      last_name: request.last_name,
      email: request.email,
      events: request.events,
    })
    .select('*')
    .single();

  if (error) throw new Error(error.message);
  return data as EmailConfiguration;
}

export async function updateEmailConfiguration(
  request: UpdateEmailConfigRequest,
): Promise<EmailConfiguration> {
  const updates: Record<string, unknown> = {
    updated_at: new Date().toISOString(),
  };
  if (request.first_name !== undefined) updates.first_name = request.first_name;
  if (request.last_name !== undefined) updates.last_name = request.last_name;
  if (request.email !== undefined) updates.email = request.email;
  if (request.events !== undefined) updates.events = request.events;
  if (request.is_active !== undefined) updates.is_active = request.is_active;

  const { data, error } = await supabase
    .from('outlet_email_configurations')
    .update(updates)
    .eq('id', request.id)
    .select('*')
    .single();

  if (error) throw new Error(error.message);
  return data as EmailConfiguration;
}

export async function deleteEmailConfiguration(id: string): Promise<void> {
  const { error } = await supabase
    .from('outlet_email_configurations')
    .delete()
    .eq('id', id);

  if (error) throw new Error(error.message);
}
